import React, { useEffect, useState, useRef, lazy } from 'react';
import { MapPinIcon, PhoneIcon, MailIcon, ClockIcon, AlertCircleIcon, CheckCircleIcon } from 'lucide-react';
import { SEO } from '../components/SEO';
import { openingHours } from '../data/businessInfo';
export const ContactPage = () => {
  // Generate random math challenge
  const generateMathChallenge = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    return {
      question: `${num1} + ${num2}`,
      answer: num1 + num2
    };
  };
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'informatie',
    message: '',
    honeypot: '',
    captchaAnswer: ''
  });
  // Captcha state
  const [captcha, setCaptcha] = useState(generateMathChallenge());
  // Form status state
  const [formStatus, setFormStatus] = useState({
    isSubmitting: false,
    isSubmitted: false,
    isError: false,
    message: '',
    attempts: 0,
    lastSubmitTime: 0
  });
  // Timestamp for form rendering (anti-bot measure)
  const [formRenderedAt] = useState(Date.now());
  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Generate HTML email template
  const generateEmailHTML = (data: typeof formData) => {
    const subjectLabels: {
      [key: string]: string;
    } = {
      informatie: 'Informatie aanvragen',
      afspraak: 'Afspraak maken',
      offerte: 'Offerte aanvragen',
      anders: 'Anders'
    };
    return `
<!DOCTYPE html>
<html lang="nl">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Contactformulier - BKCustoms</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f5f5f5;">
  <table role="presentation" style="width: 100%; border-collapse: collapse; background-color: #f5f5f5;">
    <tr>
      <td style="padding: 40px 20px;">
        <table role="presentation" style="width: 100%; max-width: 600px; margin: 0 auto; background-color: #ffffff; border-collapse: collapse; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
          <!-- Header -->
          <tr>
            <td style="padding: 40px 40px 30px 40px; text-align: center; border-bottom: 3px solid #D6281F;">
              <h1 style="margin: 0; font-size: 32px; font-weight: 700; color: #0a0a0a; letter-spacing: -0.5px;">
                BKCustoms
              </h1>
              <p style="margin: 8px 0 0 0; font-size: 16px; color: #666666; font-weight: 400;">
                Contactformulier inzending
              </p>
            </td>
          </tr>
          <!-- Content -->
          <tr>
            <td style="padding: 40px;">
              <table role="presentation" style="width: 100%; border-collapse: collapse;">
                <tr>
                  <td style="padding-bottom: 24px;">
                    <p style="margin: 0; font-size: 13px; text-transform: uppercase; letter-spacing: 1px; color: #999999; font-weight: 600;">Naam</p>
                    <p style="margin: 4px 0 0 0; font-size: 16px; color: #0a0a0a;">${data.name}</p>
                  </td>
                </tr>
                <tr>
                  <td style="padding-bottom: 24px;">
                    <p style="margin: 0; font-size: 13px; text-transform: uppercase; letter-spacing: 1px; color: #999999; font-weight: 600;">E-mail</p>
                    <p style="margin: 4px 0 0 0; font-size: 16px;">
                      <a href="mailto:${data.email}" style="color: #D6281F; text-decoration: none;">${data.email}</a>
                    </p>
                  </td>
                </tr>
                ${data.phone ? `
                <tr>
                  <td style="padding-bottom: 24px;">
                    <p style="margin: 0; font-size: 13px; text-transform: uppercase; letter-spacing: 1px; color: #999999; font-weight: 600;">Telefoonnummer</p>
                    <p style="margin: 4px 0 0 0; font-size: 16px;">
                      <a href="tel:${data.phone}" style="color: #D6281F; text-decoration: none;">${data.phone}</a>
                    </p>
                  </td>
                </tr>
                ` : ''}
                <tr>
                  <td style="padding-bottom: 24px;">
                    <p style="margin: 0; font-size: 13px; text-transform: uppercase; letter-spacing: 1px; color: #999999; font-weight: 600;">Onderwerp</p>
                    <p style="margin: 4px 0 0 0; font-size: 16px; color: #0a0a0a;">${subjectLabels[data.subject] || data.subject}</p>
                  </td>
                </tr>
                <tr>
                  <td style="padding-bottom: 24px;">
                    <p style="margin: 0; font-size: 13px; text-transform: uppercase; letter-spacing: 1px; color: #999999; font-weight: 600;">Bericht</p>
                    <p style="margin: 4px 0 0 0; font-size: 16px; color: #0a0a0a; line-height: 1.6; white-space: pre-wrap;">${data.message}</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <!-- Footer -->
          <tr>
            <td style="padding: 30px 40px; background-color: #f9f9f9; border-top: 1px solid #e5e5e5;">
              <p style="margin: 0 0 16px 0; font-size: 12px; color: #999999; line-height: 1.5;">
                Verzonden op ${new Date().toLocaleDateString('nl-NL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })} om ${new Date().toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit'
    })}
              </p>
              <p style="margin: 0; font-size: 12px; color: #666666; line-height: 1.5;">
                <strong style="color: #0a0a0a;">BKCustoms Car Styling</strong><br>
                Microfoonstraat 36, 1322 BN Almere<br>
                <a href="tel:+31627888306" style="color: #D6281F; text-decoration: none;">+31 (0)6 278 88 306</a> • 
                <a href="mailto:info@bkcustoms.nl" style="color: #D6281F; text-decoration: none;">info@bkcustoms.nl</a><br>
                <a href="https://www.bkcustoms.nl" style="color: #D6281F; text-decoration: none;">www.bkcustoms.nl</a>
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
    `.trim();
  };
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Check if form was filled out too quickly (likely a bot)
    const timeElapsed = Date.now() - formRenderedAt;
    if (timeElapsed < 3000) {
      setFormStatus({
        ...formStatus,
        isError: true,
        message: 'Formulier te snel ingevuld. Probeer het opnieuw.'
      });
      return;
    }
    // Check if honeypot field was filled (bot trap)
    if (formData.honeypot) {
      // Silently reject the submission but pretend it was successful
      setFormStatus({
        ...formStatus,
        isSubmitting: false,
        isSubmitted: true,
        message: 'Bedankt voor uw bericht. We nemen zo spoedig mogelijk contact met u op.'
      });
      return;
    }
    // Verify CAPTCHA
    const userAnswer = parseInt(formData.captchaAnswer);
    if (isNaN(userAnswer) || userAnswer !== captcha.answer) {
      setFormStatus({
        ...formStatus,
        isError: true,
        message: 'Onjuist antwoord op de verificatievraag. Probeer het opnieuw.'
      });
      // Generate new challenge
      setCaptcha(generateMathChallenge());
      setFormData(prev => ({
        ...prev,
        captchaAnswer: ''
      }));
      return;
    }
    // Rate limiting check
    const now = Date.now();
    const timeSinceLastSubmit = now - formStatus.lastSubmitTime;
    if (formStatus.attempts >= 3 && timeSinceLastSubmit < 60000) {
      setFormStatus({
        ...formStatus,
        isError: true,
        message: 'Te veel pogingen. Probeer het over een minuut opnieuw.'
      });
      return;
    }
    // Basic form validation
    if (!formData.name || !formData.email || !formData.message) {
      setFormStatus({
        ...formStatus,
        isError: true,
        message: 'Vul alle verplichte velden in.'
      });
      return;
    }
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFormStatus({
        ...formStatus,
        isError: true,
        message: 'Vul een geldig e-mailadres in.'
      });
      return;
    }
    // Set submitting state
    setFormStatus({
      ...formStatus,
      isSubmitting: true,
      isError: false,
      message: ''
    });
    try {
      // Generate dynamic subject line
      const subjectLabels: {
        [key: string]: string;
      } = {
        informatie: 'Informatie aanvragen',
        afspraak: 'Afspraak maken',
        offerte: 'Offerte aanvragen',
        anders: 'Anders'
      };
      const dynamicSubject = `Nieuw bericht: ${subjectLabels[formData.subject] || formData.subject} - ${formData.name}`;
      // Prepare webhook payload
      const webhookPayload = {
        subject: dynamicSubject,
        sender: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone || 'Niet opgegeven'
        },
        formFields: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone || 'Niet opgegeven',
          subject: formData.subject,
          message: formData.message
        },
        htmlEmail: generateEmailHTML(formData),
        submittedAt: new Date().toISOString(),
        timestamp: now
      };
      // Send to webhook
      const response = await fetch('https://hook.eu2.make.com/e8n7hb0m6murkud23ghvivzfho5r1qyu', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(webhookPayload)
      });
      if (!response.ok) {
        throw new Error('Webhook request failed');
      }
      // Reset form and update status
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: 'informatie',
        message: '',
        honeypot: '',
        captchaAnswer: ''
      });
      // Generate new captcha for next submission
      setCaptcha(generateMathChallenge());
      setFormStatus({
        isSubmitting: false,
        isSubmitted: true,
        isError: false,
        message: 'Bedankt voor uw bericht. We nemen zo spoedig mogelijk contact met u op.',
        attempts: formStatus.attempts + 1,
        lastSubmitTime: now
      });
    } catch (error) {
      setFormStatus({
        ...formStatus,
        isSubmitting: false,
        isError: true,
        message: 'Er is iets misgegaan. Probeer het later opnieuw.',
        attempts: formStatus.attempts + 1,
        lastSubmitTime: now
      });
    }
  };
  return <main data-id="element-615" data-mp="3vra3099njjze">
      <SEO title="Contact BKCustoms Almere | Elke dag open 09:00-18:00" description="Neem contact op met BKCustoms in Almere: Microfoonstraat 36, elke dag open van 09:00 tot 18:00. Bel, WhatsApp of stuur een bericht voor advies of een offerte." canonical="/contact" includeLocalBusiness data-id="element-616" data-mp="3vrjih2x2m8qu" />
      {/* Header Banner */}
      <div className="relative bg-[#0a0a0a] py-20" data-id="element-617" data-mp="3qqm471f6lyum">
        <div className="absolute inset-0 overflow-hidden" data-id="element-618" data-mp="2adfg35u5jowd">
          <div className="absolute inset-0 bg-black/80 z-10" data-id="element-619" data-mp="2ag1n94d4s2e2"></div>
          <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="Contact BKCustoms" className="w-full h-full object-cover object-center" data-id="element-620" data-mp="2fdtd7su7tefm" />
        </div>
        <div className="relative z-20 max-w-7xl mx-auto px-6 lg:px-8 text-center" data-id="element-621" data-mp="c2n87scrpqnn">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-622" data-mp="74vin4zligmm">
            Contact <span className="text-[#D6281F]" data-id="element-623" data-mp="27dbnk552bh9">Opnemen</span>
          </h1>
          <p className="text-white/80 max-w-2xl mx-auto text-lg" data-id="element-624" data-mp="3u9l77gvzh54a">
            Heeft u vragen of wilt u een afspraak maken? Neem contact met ons
            op.
          </p>
        </div>
      </div>
      {/* Contact Section */}
      <section className="bg-[#0a0a0a] py-20" data-id="element-625" data-mp="3kd8dwk4cota2">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-626" data-mp="1njn1orauqbj9">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-627" data-mp="1nibvkktxoroz">
            {/* Contact Form */}
            <div data-id="element-628" data-mp="1il3eknkiutpb">
              <div className="bg-[#0f0f0f] border border-[#222] rounded-lg p-8" data-id="element-629" data-mp="1iih7sq5ggi85">
                <h2 className="text-2xl font-bold text-white mb-6" data-id="element-630" data-mp="1q3jo7zshl35q">
                  Stuur ons een bericht
                </h2>
                {formStatus.isSubmitted ? <div className="bg-green-500/10 border border-green-500/30 rounded-md p-4 mb-6 flex items-start" data-id="element-631" data-mp="1zuxjfvdengt2">
                    <CheckCircleIcon className="text-green-500 mr-3 mt-0.5 flex-shrink-0" size={18} data-id="element-632" data-mp="24ro1go8rsmh4" />
                    <p className="text-white/90" data-id="element-633" data-mp="1c3s5jlr0yyle">{formStatus.message}</p>
                  </div> : formStatus.isError ? <div className="bg-red-500/10 border border-red-500/30 rounded-md p-4 mb-6 flex items-start" data-id="element-634" data-mp="1h1tom6ogaxwv">
                    <AlertCircleIcon className="text-red-500 mr-3 mt-0.5 flex-shrink-0" size={18} data-id="element-635" data-mp="1m3sov7ow9epl" />
                    <p className="text-white/90" data-id="element-636" data-mp="2xgli6mxc2b7r">{formStatus.message}</p>
                  </div> : null}
                <form onSubmit={handleSubmit} className="contact-form" data-id="element-637" data-mp="2v3hwbdunbnzy">
                  <input type="hidden" name="formRenderedAt" value={formRenderedAt} data-id="element-638" data-mp="2r261mtwx0ckw" />
                  <div className="hidden-field" aria-hidden="true" style={{
                  display: 'none'
                }} data-id="element-639" data-mp="3022oqr3np3du">
                    <input type="text" name="honeypot" id="contact-check" autoComplete="off" value={formData.honeypot} onChange={handleChange} tabIndex={-1} data-id="element-640" data-mp="3a0s8kpf44xwq" />
                  </div>
                  <div className="mb-6" data-id="element-641" data-mp="1iciy6i8syvc">
                    <label htmlFor="contact-name" className="block text-white/80 mb-2 text-sm" data-id="element-642" data-mp="3sr9vgdd9kza6">
                      Naam <span className="text-[#D6281F]" data-id="element-643" data-mp="g5b97y5d5n8h">*</span>
                    </label>
                    <input type="text" id="contact-name" name="name" value={formData.name} onChange={handleChange} className="w-full bg-[#0a0a0a] border border-[#333] rounded-md px-4 py-3 text-white focus:outline-none focus:border-[#D6281F]" required data-id="element-644" data-mp="k6n0e83vygis" />
                  </div>
                  <div className="mb-6" data-id="element-645" data-mp="3jonr2ouvd1y7">
                    <label htmlFor="contact-email" className="block text-white/80 mb-2 text-sm" data-id="element-646" data-mp="3jppcnn9ih6u6">
                      E-mail <span className="text-[#D6281F]" data-id="element-647" data-mp="39qq5k94e9j9a">*</span>
                    </label>
                    <input type="email" id="contact-email" name="email" value={formData.email} onChange={handleChange} className="w-full bg-[#0a0a0a] border border-[#333] rounded-md px-4 py-3 text-white focus:outline-none focus:border-[#D6281F]" required data-id="element-648" data-mp="3ep1fskylpvs1" />
                  </div>
                  <div className="mb-6" data-id="element-649" data-mp="2gtfk3cf6lmdx">
                    <label htmlFor="contact-phone" className="block text-white/80 mb-2 text-sm" data-id="element-650" data-mp="3ejailmkue35j">
                      Telefoonnummer
                    </label>
                    <input type="tel" id="contact-phone" name="phone" value={formData.phone} onChange={handleChange} className="w-full bg-[#0a0a0a] border border-[#333] rounded-md px-4 py-3 text-white focus:outline-none focus:border-[#D6281F]" data-id="element-651" data-mp="34j9vdpjur2w2" />
                  </div>
                  <div className="mb-6" data-id="element-652" data-mp="3mhc5e9qaotx">
                    <label htmlFor="contact-subject" className="block text-white/80 mb-2 text-sm" data-id="element-653" data-mp="8mvjeu3i9uqw">
                      Onderwerp
                    </label>
                    <select id="contact-subject" name="subject" value={formData.subject} onChange={handleChange} className="w-full bg-[#0a0a0a] border border-[#333] rounded-md px-4 py-3 text-white focus:outline-none focus:border-[#D6281F]" data-id="element-654" data-mp="ie8ahjhhcgvv">
                      <option value="informatie" data-id="element-655" data-mp="11cua0txclzxo">Informatie aanvragen</option>
                      <option value="afspraak" data-id="element-656" data-mp="2y4kuaoh5rpaw">Afspraak maken</option>
                      <option value="offerte" data-id="element-657" data-mp="2y6fge6461ngk">Offerte aanvragen</option>
                      <option value="anders" data-id="element-658" data-mp="334gv6euk70m8">Anders</option>
                    </select>
                  </div>
                  <div className="mb-6" data-id="element-659" data-mp="18u92djxpeqnx">
                    <label htmlFor="contact-message" className="block text-white/80 mb-2 text-sm" data-id="element-660" data-mp="13wqorg8qrn9l">
                      Bericht <span className="text-[#D6281F]" data-id="element-661" data-mp="1inpu8iea5voy">*</span>
                    </label>
                    <textarea id="contact-message" name="message" value={formData.message} onChange={handleChange} rows={5} className="w-full bg-[#0a0a0a] border border-[#333] rounded-md px-4 py-3 text-white focus:outline-none focus:border-[#D6281F]" required data-id="element-662" data-mp="1dnbwbt6e824n"></textarea>
                  </div>
                  {/* Simple CAPTCHA */}
                  <div className="mb-6" data-id="element-663" data-mp="2bcezmtmdcn6i">
                    <label htmlFor="contact-captcha" className="block text-white/80 mb-2 text-sm" data-id="element-664" data-mp="26bqyhf4fepyk">
                      Verificatie: Hoeveel is {captcha.question}?{' '}
                      <span className="text-[#D6281F]" data-id="element-665" data-mp="2ivehuvy3g8oa">*</span>
                    </label>
                    <input type="text" id="contact-captcha" name="captchaAnswer" value={formData.captchaAnswer} onChange={handleChange} className="w-full bg-[#0a0a0a] border border-[#333] rounded-md px-4 py-3 text-white focus:outline-none focus:border-[#D6281F]" placeholder="Vul het antwoord in" required autoComplete="off" data-id="element-666" data-mp="24vdffc26kt6p" />
                  </div>
                  <div className="mb-6 p-3 bg-[#0a0a0a] border border-[#333] rounded-md" data-id="element-667" data-mp="3fjkhf4g5savp">
                    <p className="text-white/70 text-sm" data-id="element-668" data-mp="3kakec9pa2sy7">
                      Om spam te voorkomen, controleren wij handmatig alle
                      berichten. Vul alstublieft alle verplichte velden in.
                    </p>
                  </div>
                  <button type="submit" disabled={formStatus.isSubmitting} className={`w-full bg-[#D6281F] hover:bg-[#D6281F]/90 text-white py-3 rounded-md transition-all duration-300 font-medium ${formStatus.isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-669" data-mp="1kou58cln2xek">
                    {formStatus.isSubmitting ? 'Bezig met versturen...' : 'Versturen'}
                  </button>
                  <p className="text-white/50 text-xs mt-4" data-id="element-670" data-mp="n3oc38a9jtkg">
                    Door dit formulier te versturen gaat u akkoord met ons{' '}
                    <a href="/privacybeleid" className="text-[#D6281F] hover:underline" data-id="element-671" data-mp="2hg4jwdipjfrf">
                      privacybeleid
                    </a>
                    .
                  </p>
                </form>
              </div>
            </div>
            {/* Contact Info & Map */}
            <div data-id="element-672" data-mp="5uceds5kz53e">
              <div className="bg-[#0f0f0f] border border-[#222] rounded-lg p-8 mb-8" data-id="element-673" data-mp="5qohqk4ua2kg">
                <h2 className="text-2xl font-bold text-white mb-6" data-id="element-674" data-mp="kjiah794gf72">
                  Contactgegevens
                </h2>
                <div className="space-y-6" data-id="element-675" data-mp="ti3nkmvtr20g">
                  <div className="flex items-start" data-id="element-676" data-mp="tgsmiutrk8d1">
                    <MapPinIcon size={24} className="text-[#D6281F] mr-4 mt-1 flex-shrink-0" data-id="element-677" data-mp="pgigji5z1hy6" />
                    <div data-id="element-678" data-mp="13frndr8aezph">
                      <h3 className="text-white font-medium mb-1" data-id="element-679" data-mp="13dx9gwvfo7uw">Adres</h3>
                      <p className="text-white/80" data-id="element-680" data-mp="yhztwsrzv5j6">
                        Microfoonstraat 36
                        <br data-id="element-681" data-mp="35869l2hv6qbp" />
                        1322 BN Almere
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-682" data-mp="145onwh1k00p2">
                    <PhoneIcon size={24} className="text-[#D6281F] mr-4 mt-1 flex-shrink-0" data-id="element-683" data-mp="146zsgj2tpch8" />
                    <div data-id="element-684" data-mp="1d5lhmx2svpqz">
                      <h3 className="text-white font-medium mb-1" data-id="element-685" data-mp="1d5brn9hr9ila">Telefoon</h3>
                      <p className="text-white/80" data-id="element-686" data-mp="1i5g9b2uegjd3">
                        <a href="tel:+31627888306" className="hover:text-white transition-colors" data-id="element-687" data-mp="1i33ie06nyynw">
                          +31 (0)6 278 88 306
                        </a>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-688" data-mp="20vfuvfr17gbr">
                    <MailIcon size={24} className="text-[#D6281F] mr-4 mt-1 flex-shrink-0" data-id="element-689" data-mp="20v659t9wfb6l" />
                    <div data-id="element-690" data-mp="2atc585c5a78t">
                      <h3 className="text-white font-medium mb-1" data-id="element-691" data-mp="2aun69xe7h0w8">E-mail</h3>
                      <p className="text-white/80" data-id="element-692" data-mp="2ky29wiyxnpgq">
                        <a href="mailto:info@bkcustoms.nl" className="hover:text-white transition-colors" data-id="element-693" data-mp="2kvpjrij0u8sl">
                          info@bkcustoms.nl
                        </a>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-694" data-mp="33o0f8rbcj4go">
                    <ClockIcon size={24} className="text-[#D6281F] mr-4 mt-1 flex-shrink-0" data-id="element-695" data-mp="33nrzmrnu9l1s" />
                    <div data-id="element-696" data-mp="2txyv2am46671">
                      <h3 className="text-white font-medium mb-1" data-id="element-697" data-mp="2tx5rfiigbwt4">
                        Openingstijden
                      </h3>
                      <p className="text-white/80" data-id="element-698" data-mp="2pv2gcuzp2u8d">
                        {openingHours.lines[0]}
                        <br data-id="element-699" data-mp="3mgm9562p0kym" />
                        {openingHours.lines[1]}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Map */}
              <div className="bg-[#0f0f0f] border border-[#222] rounded-lg overflow-hidden h-80" data-id="element-700" data-mp="374uy26d89ixk">
                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2435.4380679053694!2d5.221333776885494!3d52.37541904430272!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c617b0c02a7c11%3A0x40c8a23d1e7a2f5d!2sMicrofoonstraat%2036%2C%201322%20BN%20Almere!5e0!3m2!1sen!2snl!4v1685889643728!5m2!1sen!2snl" width="100%" height="100%" style={{
                border: 0
              }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="BKCustoms locatie" data-id="element-701" data-mp="2t5mvr4lrpup5"></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>;
};